import { z } from 'zod'

export const MIN_CHAT_DRAWER_WIDTH = 400
export const MAX_CHAT_DRAWER_WIDTH = 1200

export const NeutronChatIconModeSchema = z.enum([
  'neutron',
  'blackhole-violet',
  'blackhole-gold',
])

export const NeutronChatAnchorSchema = z.enum(['left', 'right'])

export const OpsConsoleAvaPreferencesSchema = z.object({
  hideAssistant: z.boolean(),
})

export const OpsConsoleNeutronChatDrawerPreferencesSchema = z.object({
  width: z.number().int().min(MIN_CHAT_DRAWER_WIDTH).max(MAX_CHAT_DRAWER_WIDTH),
  iconMode: NeutronChatIconModeSchema,
  darkMode: z.boolean(),
  anchor: NeutronChatAnchorSchema,
  locked: z.boolean(),
})

export const OpsConsoleNeutronChatPreferencesSchema = z.object({
  hotkeyKey: z.string().regex(/^[a-z]$/),
  drawer: OpsConsoleNeutronChatDrawerPreferencesSchema,
  wizardCompleted: z.boolean(),
  browserNotificationsEnabled: z.boolean(),
})

export const OpsConsolePreferencesSchema = z.object({
  ava: OpsConsoleAvaPreferencesSchema,
  neutronChat: OpsConsoleNeutronChatPreferencesSchema,
})

export const OpsConsolePreferencesPatchSchema = z.object({
  ava: OpsConsoleAvaPreferencesSchema.partial().optional(),
  neutronChat: z
    .object({
      hotkeyKey: z
        .string()
        .regex(/^[a-z]$/)
        .optional(),
      drawer: OpsConsoleNeutronChatDrawerPreferencesSchema.partial().optional(),
      wizardCompleted: z.boolean().optional(),
      browserNotificationsEnabled: z.boolean().optional(),
    })
    .optional(),
})

export type NeutronChatIconMode = z.infer<typeof NeutronChatIconModeSchema>
export type NeutronChatAnchor = z.infer<typeof NeutronChatAnchorSchema>
export type OpsConsoleAvaPreferences = z.infer<
  typeof OpsConsoleAvaPreferencesSchema
>
export type OpsConsoleNeutronChatDrawerPreferences = z.infer<
  typeof OpsConsoleNeutronChatDrawerPreferencesSchema
>
export type OpsConsoleNeutronChatPreferences = z.infer<
  typeof OpsConsoleNeutronChatPreferencesSchema
>
export type OpsConsolePreferences = z.infer<typeof OpsConsolePreferencesSchema>
export type OpsConsolePreferencesPatch = z.infer<
  typeof OpsConsolePreferencesPatchSchema
>

export const DEFAULT_OPS_CONSOLE_PREFERENCES: OpsConsolePreferences =
  OpsConsolePreferencesSchema.parse({
    ava: { hideAssistant: false },
    neutronChat: {
      hotkeyKey: 'l',
      drawer: {
        width: 624,
        iconMode: 'neutron',
        darkMode: false,
        anchor: 'right',
        locked: true,
      },
      wizardCompleted: false,
      browserNotificationsEnabled: true,
    },
  })

export function mergeOpsConsolePreferencesPatch(
  existing: OpsConsolePreferences,
  patch: OpsConsolePreferencesPatch,
): OpsConsolePreferences {
  return OpsConsolePreferencesSchema.parse({
    ava: { ...existing.ava, ...patch.ava },
    neutronChat: {
      ...existing.neutronChat,
      ...patch.neutronChat,
      drawer: {
        ...existing.neutronChat.drawer,
        ...patch.neutronChat?.drawer,
      },
    },
  })
}
